import { useEffect, useId, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Navigate, useHref, useLocation } from "react-router-dom";
import { passwordProblems, passwordRuleList, VERIFICATION_EMAIL_SUBJECT, type PasswordPolicy } from "@starsach/shared-types";
import { useAuth } from "../auth/AuthProvider";
import { isSigningOut, setMockSession } from "../auth/auth";
import { cognitoApi, passkeyAssertion, passkeysPossible } from "../auth/cognito-api";
import { loadContextScript, userContextData } from "../auth/context-data";
import { deviceVerifier } from "../auth/device-verifier";
import { mockPasskeyAssertion } from "../auth/mock-passkey";
import { enrollPasskey } from "../auth/passkey-enroll";
import { hasPendingSignOut } from "../auth/pending-sign-out";
import { readRememberedDevice, removeRememberedDevice, writeRememberedDevice } from "../auth/session-store";
import { groupSecret, spellSecret, stillNeeded } from "../auth/setup-words";
import { deviceNameFromUserAgent, FINISHING_SLOW_WORDS, SIGN_IN_ERROR_COPY, SIGN_IN_NOTICE_COPY, SignInMachine, type SignInDeps, type SignInErrorCode, type SignInState } from "../auth/sign-in-machine";
import { PoweredBy, StarsachLockup } from "../components/Brand";
import { LoadingState, btnPrimary, btnSecondary, inputClass } from "../components/ui";
import { isMock, passwordPolicyOf, type AppConfig } from "../config";
import { otpauthUri, portalHost } from "../auth/otpauth";
import { ssoRouteFor } from "../lib/sso";
import { mockCreateCredential } from "../mocks/passkeys";
import { viewAsFromLocation } from "../viewAs";

/**
 * Starsach's own sign-in screen (crmarr/templates auth-self-hosted-sign-in.md, R1, R2, R10, R11), the steps of the
 * sign-in machine (auth/sign-in-machine.ts, ported from gairm/lms 596e644) in Starsach's card. One step at a time: the
 * email first - an organization that signs in through its own provider goes there (ADR-0014) - then the passkey, or the
 * password and the authenticator code; a first sign-in chooses its password, sets up its authenticator app and is
 * offered a passkey; Forgot your password? resets it here with a code by email; then in.
 *
 * Accessibility by rule: every step has one h1, and focus moves to it whenever the step changes, with the tab's title
 * following; an error is shown beside the field it belongs to, wired by aria-describedby and aria-invalid, or above the
 * form as an alert; good news is a status; every control has a visible label; the fields carry the autocomplete tokens
 * password managers read (`username webauthn`, `current-password`, `new-password`, `one-time-code`); a code field takes a
 * paste with spaces and never submits itself; the authenticator's key is given as text as well as a QR code.
 * "Trouble signing in?" stays under the card on every step.
 */
export function LoginPage() {
  const { status, config } = useAuth();
  const location = useLocation();
  // useHref folds in the router's basename, so a view-as tab (ADR-0011) comes back to the customer's portal, not the root.
  // The query and fragment come back too: a deep link (an email's /account#email, its confirmation token) survives a sign-in.
  const wanted = (location.state as { from?: { pathname: string; search?: string; hash?: string } } | null)?.from;
  const from = useHref(wanted ? { pathname: wanted.pathname, search: wanted.search ?? "", hash: wanted.hash ?? "" } : "/");
  // Trouble signing in? can send the person straight to the reset: /login?reset=password.
  const startAt = new URLSearchParams(location.search).get("reset") === "password" ? "forgot" : undefined;
  if (status === "signed-in") return <Navigate to={from} replace />;
  // A session may already exist (a reload, another tab's sign-in): showing the form while /v1/me is fetched reads as signed out.
  if (status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas dark:bg-neutral-950">
        <LoadingState label="Signing in" />
      </main>
    );
  }
  // R4: between a sign-out's clearing the browser and leaving, no sign-in may start.
  if (isSigningOut()) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas dark:bg-neutral-950">
        <LoadingState label="Signing out" />
      </main>
    );
  }
  // ADR-0011: a view-as tab shares the browser's session; with none, the person signs in on the main portal first.
  if (viewAsFromLocation()) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">This tab has no session</h1>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">A customer's portal opens from your own signed-in portal and uses that sign-in. Sign in there, then open "View their portal" again.</p>
        <a href="/" className={`${btnPrimary} mt-8 w-full py-3 text-base`}>
          Go to the portal
        </a>
      </Shell>
    );
  }
  return (
    <Shell>
      {config && <SignInSteps config={config} from={from} startAt={startAt} />}
      {isMock && <MockPersonas from={from} />}
    </Shell>
  );
}

/** The card: the lockup, the step, and underneath "Trouble signing in?" and Powered by (the owner, 1 Oct). */
function Shell({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-canvas px-4 py-12 sm:px-6 dark:bg-neutral-950">
      <div className="w-full min-w-0 max-w-md rounded-2xl bg-white px-5 py-8 shadow-lg ring-1 ring-neutral-200 sm:px-8 sm:py-10 dark:bg-neutral-900 dark:ring-neutral-800">
        <div className="mb-6 flex justify-center">
          <StarsachLockup className="h-12 w-auto text-ink dark:text-white" />
        </div>
        {/* #621: the last sign-out here did not reach Starsach in time; it is finished at the next sign-in. */}
        {hasPendingSignOut() && (
          <p role="status" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
            You are signed out in this browser, but Starsach did not answer in time to end the sign-in everywhere it was used. It finishes when you next sign in here; until then, a copy of that sign-in elsewhere can keep working for up to 15 minutes.
          </p>
        )}
        {children}
      </div>
      <p className="mt-6 text-center text-sm">
        <a className="text-accent-strong underline dark:text-accent-bright" href="/sign-in-help">
          Trouble signing in?
        </a>
      </p>
      <PoweredBy className="mt-6" />
    </main>
  );
}

function SignInSteps({ config, from, startAt }: { config: AppConfig; from: string; startAt?: "forgot" | undefined }) {
  const { acceptSignIn, signIn } = useAuth();
  const [returnTo] = useState(from);
  const policy = passwordPolicyOf(config);
  const [machine] = useState(() => {
    const api = cognitoApi(config);
    const deps: SignInDeps = {
      api,
      passwordPolicy: policy,
      // First sign-in's passkey offer runs Account > Security's enrollment.
      enrollPasskey: (accessToken, signal) => enrollPasskey(api, accessToken, isMock ? mockCreateCredential : undefined, signal),
      passkeysPossible,
      passkeyAssertion: isMock ? mockPasskeyAssertion : passkeyAssertion,
      contextData: isMock ? async () => ({ EncodedData: "mock-context-data" }) : (username) => userContextData(config, username),
      deviceVerifier: (groupKey, deviceKey) => deviceVerifier(groupKey, deviceKey),
      deviceName: () => deviceNameFromUserAgent(navigator.userAgent),
      // "Remember this device" (R11): kept per address in this browser, beside (and outliving) the session.
      rememberedDevices: { read: readRememberedDevice, write: writeRememberedDevice, remove: (email) => removeRememberedDevice({ email }) },
      complete: async (result) => {
        if (!(await acceptSignIn(result))) throw new Error("The session layer did not take the sign-in");
        // A full navigation: the provider reads the session and /v1/me from scratch.
        window.location.replace(returnTo);
      },
      // ADR-0014: the address is hashed and compared in the browser against the registered domains; it is not sent to us.
      toProvider: async (email) => {
        if (config.ssoDomainHashes.length === 0) return false;
        const idpIdentifier = await ssoRouteFor(email, config.ssoDomainHashes);
        if (!idpIdentifier) return false;
        void signIn(returnTo, { idpIdentifier, loginHint: email });
        return true;
      },
    };
    const m = new SignInMachine(deps);
    if (startAt === "forgot") m.forgotPassword();
    return m;
  });
  const state = useSyncExternalStore(
    (cb) => machine.subscribe(cb),
    () => machine.state,
  );

  // R8: the advanced-security script, fetched while the person types the address, so Continue does not wait on it.
  useEffect(() => {
    if (!isMock) void loadContextScript(config);
  }, [config]);

  // Every step change moves focus to the step's heading and names it in the tab's title; never on the first render.
  const root = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  useEffect(() => {
    const h1 = root.current?.querySelector<HTMLElement>("h1");
    if (h1) document.title = `${h1.textContent?.trim() ?? ""} · Starsach`;
    if (first.current) {
      first.current = false;
      return;
    }
    h1?.focus();
  }, [state.step.name]);

  return (
    <div ref={root} data-testid="sign-in" data-step={state.step.name}>
      <Step state={state} machine={machine} policy={policy} />
    </div>
  );
}

/** Errors that belong to the field on the step; any other is shown above the form. */
const FIELD_ERRORS = new Set<SignInErrorCode>(["email-missing", "password-missing", "wrong-password", "code-format", "wrong-code", "code-used", "password-rules", "password-rejected", "email-code-format", "email-code-wrong", "email-code-expired"]);

/** On the reset step, the errors that belong to the code field; the rest of the field errors are the new password's. */
const CODE_ERRORS = new Set<SignInErrorCode>(["email-code-format", "email-code-wrong", "email-code-expired"]);

function Heading({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="mb-6 text-center">
      <h1 tabIndex={-1} className="text-2xl font-semibold outline-none">
        {children}
      </h1>
      {sub && <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{sub}</p>}
    </div>
  );
}

type FieldProps = { label: string; error?: string | undefined; description?: string; describedBy?: string; attempt?: number } & React.InputHTMLAttributes<HTMLInputElement>;

function Field({ label, error, description, describedBy: extra, attempt = 0, ...input }: FieldProps) {
  const id = useId();
  const describedBy = [extra ?? "", description ? `${id}-d` : "", error ? `${id}-e` : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {description && (
        <p id={`${id}-d`} className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
          {description}
        </p>
      )}
      <input id={id} className={inputClass} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...input} />
      {error && (
        // Keyed by the attempt, so the same error after another try is said again.
        <p key={attempt} id={`${id}-e`} role="alert" className="mt-1 text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}

/** The error under a step's heading when it is not about one field (a canceled passkey, a lockout). */
function StepError({ state, attempt }: { state: SignInState; attempt: number }) {
  if (!state.error || FIELD_ERRORS.has(state.error)) return null;
  return (
    <p key={attempt} role="alert" data-testid="sign-in-error" className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
      {SIGN_IN_ERROR_COPY[state.error]}
    </p>
  );
}

/** Good news, said once as it happens: a new code sent, a password changed. */
function StepNotice({ state }: { state: SignInState }) {
  if (!state.notice) return null;
  return (
    <p role="status" data-testid="sign-in-notice" className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100">
      {SIGN_IN_NOTICE_COPY[state.notice]}
    </p>
  );
}

/**
 * "Remember this device" (R11): a real checkbox, off until the person ticks it, its label visible and its meaning in
 * words beside it, wired with aria-describedby. Shown where the code is asked for, because the code is what it skips.
 */
function RememberDevice({ checked, onChange }: { checked: boolean; onChange: (on: boolean) => void }) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <input id={id} type="checkbox" name="remember-device" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-describedby={`${id}-desc`} className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium">
          Remember this device
        </label>
        <p id={`${id}-desc`} className="text-xs text-neutral-600 dark:text-neutral-400">
          When you sign in here with your password, you won't be asked for the code from your authenticator app. This lasts until you remove this device on Account, Security, or choose Sign out everywhere. Leave it unticked on a shared or public computer.
        </p>
      </div>
    </div>
  );
}

function ShowPassword({ checked, onChange }: { checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <label className="flex min-h-6 items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5" />
      Show password
    </label>
  );
}

function Step({ state, machine, policy }: { state: SignInState; machine: SignInMachine; policy: PasswordPolicy }) {
  const [email, setEmail] = useState(state.email);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const rulesId = useId();
  // A new password's unmet rules are named, from the password as it is now; the other field errors in the machine's words.
  const fieldError =
    state.error === "password-rules" ? (stillNeeded(newPassword, policy) ?? SIGN_IN_ERROR_COPY["password-rules"]) : state.error && FIELD_ERRORS.has(state.error) ? SIGN_IN_ERROR_COPY[state.error] : undefined;
  const signingInAs = (
    <>
      Signing in as <strong className="break-all text-ink dark:text-white">{state.email}</strong>.
    </>
  );
  const clear = () => {
    setPassword("");
    setCode("");
    setNewPassword("");
  };
  const differentEmail = (
    <button
      type="button"
      className={`${btnSecondary} w-full`}
      onClick={() => {
        clear();
        machine.restart();
      }}
    >
      Use a different email address
    </button>
  );
  const backToSignIn = (
    <button
      type="button"
      className={`${btnSecondary} w-full`}
      onClick={() => {
        clear();
        machine.restart();
      }}
    >
      Back to sign in
    </button>
  );
  const submit = (fn: () => void) => (e: FormEvent) => {
    e.preventDefault();
    setAttempt((n) => n + 1);
    fn();
  };
  const primary = `${btnPrimary} w-full py-3 text-base`;
  const busy = state.busy || undefined;
  const fieldProps = { error: fieldError, attempt };

  switch (state.step.name) {
    case "email":
      return (
        <>
          <Heading>Sign in</Heading>
          <StepNotice state={state} />
          <StepError state={state} attempt={attempt} />
          <form noValidate onSubmit={submit(() => void machine.submitEmail(email))} className="space-y-4">
            <Field label="Email address" type="email" name="username" autoComplete="username webauthn" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} {...fieldProps} />
            <button type="submit" className={primary} aria-disabled={busy}>
              Continue
            </button>
          </form>
        </>
      );
    case "passkey":
      return (
        <>
          <Heading sub={signingInAs}>Use your passkey</Heading>
          <p className="text-sm">Follow the prompt from your browser or device to use your passkey. It may ask for your fingerprint, your face or your device's PIN.</p>
          {state.factors.includes("PASSWORD") && (
            <button type="button" className={`${btnSecondary} mt-4 w-full`} onClick={() => machine.usePassword()}>
              Use your password instead
            </button>
          )}
        </>
      );
    case "choose":
      return (
        <>
          <Heading sub={signingInAs}>Choose how to sign in</Heading>
          <StepError state={state} attempt={attempt} />
          <ul className="space-y-3" aria-label="Ways to sign in">
            {state.factors.includes("WEB_AUTHN") && (
              <li>
                <button type="button" className={primary} onClick={() => void machine.usePasskey()}>
                  Use your passkey
                </button>
              </li>
            )}
            {state.factors.includes("PASSWORD") && (
              <li>
                <button type="button" className={state.factors.includes("WEB_AUTHN") ? `${btnSecondary} w-full` : primary} onClick={() => machine.usePassword()}>
                  Use your password
                </button>
              </li>
            )}
            <li>{differentEmail}</li>
          </ul>
        </>
      );
    case "password":
      return (
        <>
          <Heading sub={signingInAs}>Enter your password</Heading>
          <StepError state={state} attempt={attempt} />
          <form noValidate onSubmit={submit(() => void machine.submitPassword(password))} className="space-y-4">
            {/* The address again, hidden, so a password manager saves and fills the pair. */}
            <input type="email" name="username" autoComplete="username" value={state.email} readOnly hidden />
            <Field label="Password" type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} {...fieldProps} />
            <button type="submit" className={primary} aria-disabled={busy}>
              Continue
            </button>
          </form>
          <div className="mt-3 space-y-3">
            {state.factors.includes("WEB_AUTHN") && passkeysPossible() && (
              <button type="button" className={`${btnSecondary} w-full`} onClick={() => void machine.usePasskey()}>
                Use your passkey instead
              </button>
            )}
            {differentEmail}
          </div>
          <p className="mt-4 text-center text-sm">
            <button
              type="button"
              className="inline-flex min-h-6 items-center underline"
              onClick={() => {
                clear();
                machine.forgotPassword();
              }}
            >
              Forgot your password?
            </button>
          </p>
        </>
      );
    case "code":
      return (
        <>
          <Heading sub={signingInAs}>Enter the code from your authenticator app</Heading>
          <form noValidate onSubmit={submit(() => void machine.submitCode(code))} className="space-y-4">
            <Field
              label="Code from your authenticator app"
              description="Open your authenticator app and enter the six digits it shows for Starsach. The code changes every 30 seconds."
              inputMode="numeric"
              autoComplete="one-time-code"
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              {...fieldProps}
            />
            <RememberDevice checked={state.remember} onChange={(on) => machine.setRemember(on)} />
            <button type="submit" className={primary} aria-disabled={busy}>
              Continue
            </button>
          </form>
          <div className="mt-3">{differentEmail}</div>
        </>
      );
    case "new-password":
      return (
        <>
          <Heading sub={signingInAs}>Choose your password</Heading>
          <StepError state={state} attempt={attempt} />
          <p className="mb-4 text-sm">This is your first sign-in, so choose your own password to use instead of the temporary one. Next, you'll set up your authenticator app.</p>
          <form noValidate onSubmit={submit(() => void machine.submitNewPassword(newPassword))} className="space-y-4">
            {/* The address, hidden, so a password manager saves the new password against it. */}
            <input type="email" name="username" autoComplete="username" value={state.email} readOnly hidden />
            <PasswordRules id={rulesId} password={newPassword} policy={policy} />
            <Field label="New password" type={showPassword ? "text" : "password"} name="new-password" autoComplete="new-password" autoCapitalize="none" spellCheck={false} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} describedBy={rulesId} {...fieldProps} />
            <ShowPassword checked={showPassword} onChange={setShowPassword} />
            <button type="submit" className={primary} aria-disabled={busy}>
              Continue
            </button>
          </form>
          <div className="mt-3">{differentEmail}</div>
        </>
      );
    case "authenticator":
      return (
        <>
          <Heading sub={signingInAs}>Set up your authenticator app</Heading>
          <StepError state={state} attempt={attempt} />
          {!state.step.secret ? (
            <LoadingState label="Getting your setup key" />
          ) : (
            <>
              <p className="text-sm">An authenticator app on your phone or computer shows a new six-digit code every 30 seconds. You'll enter the code each time you sign in with your password. You can add a passkey after this.</p>
              <ol className="mt-4 list-decimal space-y-2 pl-6 text-sm">
                <li>Open your authenticator app and choose to add an account.</li>
                <li>Scan the QR code, or type the setup key into the app instead.</li>
                <li>Enter the six-digit code the app shows for Starsach.</li>
              </ol>
              <SetupKey secret={state.step.secret} email={state.email} />
              <form noValidate onSubmit={submit(() => void machine.submitSetupCode(code))} className="mt-4 space-y-4">
                <Field label="Code from your authenticator app" inputMode="numeric" autoComplete="one-time-code" name="code" value={code} onChange={(e) => setCode(e.target.value)} {...fieldProps} />
                <RememberDevice checked={state.remember} onChange={(on) => machine.setRemember(on)} />
                <button type="submit" className={primary} aria-disabled={busy}>
                  Continue
                </button>
              </form>
            </>
          )}
          <div className="mt-3">{differentEmail}</div>
        </>
      );
    case "passkey-offer":
      return (
        <>
          <Heading sub="Your password and authenticator app are set up.">Add a passkey</Heading>
          <StepError state={state} attempt={attempt} />
          <p className="text-sm">A passkey lets you sign in with your fingerprint, your face or your device's PIN, with no password or code to type. It's optional. Choose Not now to skip it; you can add one later from Account, Security.</p>
          <div className="mt-4 space-y-3">
            <button
              type="button"
              className={primary}
              aria-disabled={busy}
              onClick={() => {
                setAttempt((n) => n + 1);
                void machine.addPasskey();
              }}
            >
              Add a passkey
            </button>
            {/* Not now answers even while the browser's prompt is open: it closes it and goes in. */}
            <button type="button" className={`${btnSecondary} w-full`} aria-disabled={(state.busy && !state.step.adding) || undefined} onClick={() => void machine.skipPasskey()}>
              Not now
            </button>
          </div>
        </>
      );
    case "forgot":
      return (
        <>
          <Heading>Reset your password</Heading>
          <StepError state={state} attempt={attempt} />
          <p className="mb-4 text-sm">Enter the email address you sign in with. We'll email you a code, and then you choose a new password.</p>
          <form noValidate onSubmit={submit(() => void machine.submitForgot(email))} className="space-y-4">
            <Field label="Email address" type="email" name="username" autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} {...fieldProps} />
            <button type="submit" className={primary} aria-disabled={busy}>
              Send the code
            </button>
          </form>
          <div className="mt-3">{backToSignIn}</div>
        </>
      );
    case "reset":
      return (
        <>
          <Heading
            sub={
              <>
                If <strong className="break-all text-ink dark:text-white">{state.email}</strong> has an account, we've sent a code to it.
              </>
            }
          >
            Choose a new password
          </Heading>
          <StepNotice state={state} />
          <StepError state={state} attempt={attempt} />
          <p className="mb-4 text-sm">Look for an email titled "{VERIFICATION_EMAIL_SUBJECT}", headed "Your password reset code". The code is on a line by itself. It can take a few minutes to arrive, and it may be in your junk or spam folder.</p>
          <form noValidate onSubmit={submit(() => void machine.submitReset(code, newPassword))} className="space-y-4">
            <input type="email" name="username" autoComplete="username" value={state.email} readOnly hidden />
            <Field label="Code from the email" inputMode="numeric" autoComplete="one-time-code" name="code" value={code} onChange={(e) => setCode(e.target.value)} error={state.error && CODE_ERRORS.has(state.error) ? fieldError : undefined} attempt={attempt} />
            <PasswordRules id={rulesId} password={newPassword} policy={policy} />
            <Field
              label="New password"
              type={showPassword ? "text" : "password"}
              name="new-password"
              autoComplete="new-password"
              autoCapitalize="none"
              spellCheck={false}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              describedBy={rulesId}
              error={state.error && !CODE_ERRORS.has(state.error) ? fieldError : undefined}
              attempt={attempt}
            />
            <ShowPassword checked={showPassword} onChange={setShowPassword} />
            <button type="submit" className={primary} aria-disabled={busy}>
              Save my new password
            </button>
          </form>
          <div className="mt-3 space-y-3">
            <button type="button" className={`${btnSecondary} w-full`} aria-disabled={busy} onClick={() => void machine.resendResetCode()}>
              Send a new code
            </button>
            {backToSignIn}
          </div>
          <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">No code after a few minutes, even after sending a new one? Use Trouble signing in? below, or ask your organization's managers.</p>
        </>
      );
    case "verify-email":
      return (
        <>
          <Heading
            sub={
              <>
                We've sent a code to <strong className="break-all text-ink dark:text-white">{state.email}</strong>.
              </>
            }
          >
            Confirm your email address
          </Heading>
          <StepNotice state={state} />
          <StepError state={state} attempt={attempt} />
          <p className="mb-4 text-sm">Your email address isn't confirmed yet. Confirming it means you can reset your password yourself if you ever forget it. Look for an email titled "{VERIFICATION_EMAIL_SUBJECT}", headed "Your verification code". The code is on a line by itself.</p>
          <form noValidate onSubmit={submit(() => void machine.submitEmailCode(code))} className="space-y-4">
            <Field label="Code from the email" inputMode="numeric" autoComplete="one-time-code" name="code" value={code} onChange={(e) => setCode(e.target.value)} {...fieldProps} />
            <button type="submit" className={primary} aria-disabled={state.busy || !state.step.sent || undefined}>
              Confirm my email address
            </button>
          </form>
          <div className="mt-3 space-y-3">
            <button type="button" className={`${btnSecondary} w-full`} aria-disabled={busy} onClick={() => void machine.resendEmailCode()}>
              Send a new code
            </button>
            <button type="button" className={`${btnSecondary} w-full`} aria-disabled={busy} onClick={() => void machine.skipEmailVerification()}>
              Not now
            </button>
          </div>
        </>
      );
    case "provider":
      return (
        <>
          <Heading>Going to your organization's sign-in page</Heading>
          <p className="text-sm">You sign in through your organization. You'll come straight back here after you sign in there.</p>
        </>
      );
    case "finishing":
    case "done":
      return (
        <>
          <Heading>Signing you in</Heading>
          <p className="text-sm">Getting your account ready on this device…</p>
          <p role="status" className="mt-2 text-sm">
            {state.step.name === "finishing" && state.step.slow ? FINISHING_SLOW_WORDS : ""}
          </p>
        </>
      );
  }
}

/**
 * The pool's password rules, stated before the person types, each marked in words as it is met - never by color alone.
 * Not a live region: the unmet rules are said when Continue finds them, not on every keystroke.
 */
function PasswordRules({ id, password, policy }: { id: string; password: string; policy: PasswordPolicy }) {
  const problems = passwordProblems(password, policy);
  return (
    <div id={id} data-testid="password-rules" className="rounded-lg bg-canvas p-3 text-sm dark:bg-neutral-800">
      <p className="font-medium">Your password needs:</p>
      <ul className="mt-1 space-y-1">
        {passwordRuleList(policy).map((r) => {
          const met = password.length > 0 && !problems.includes(r.rule);
          return (
            <li key={r.rule} className="flex flex-wrap items-baseline gap-x-2">
              <span className="min-w-0">{r.text}</span>
              {/* Spoken after the rule, so a screen reader reads "...: done" or "...: still needed". */}
              <span className={`text-xs font-medium ${met ? "text-emerald-700 dark:text-emerald-300" : "text-neutral-600 dark:text-neutral-400"}`}>
                <span className="sr-only">: </span>
                {met ? "done" : "still needed"}
              </span>
            </li>
          );
        })}
        {problems.includes("edge-space") && <li className="font-medium text-red-700 dark:text-red-300">No space at the start or the end</li>}
      </ul>
    </div>
  );
}

/**
 * The authenticator's secret (R1): as a QR code for a camera - hidden from assistive technology, since the same secret
 * is given in words beside it - and as text: grouped for reading, copyable in one press, spelled out character by
 * character for a screen reader, and as a link that opens an authenticator app on this device. The QR code is dark on
 * white whatever the theme.
 */
function SetupKey({ secret, email }: { secret: string; email: string }) {
  const [copied, setCopied] = useState<"yes" | "no" | undefined>();
  const host = portalHost();
  const otpauth = otpauthUri(host, email, secret);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied("yes");
    } catch {
      setCopied("no");
    }
  };
  return (
    <div className="mt-4 flex flex-wrap items-start gap-4">
      <div className="shrink-0 rounded-lg border border-neutral-200 bg-white p-3" aria-hidden="true">
        <QRCodeSVG value={otpauth} size={160} level="M" marginSize={2} bgColor="#ffffff" fgColor="#000000" />
      </div>
      <div className="min-w-0 flex-1 basis-48 space-y-2" data-testid="setup-key">
        <p className="text-sm font-medium">Setup key</p>
        <p className="text-xs text-neutral-600 dark:text-neutral-400">Can't scan the QR code? Type this setup key into the app. Capital letters and spaces don't matter.</p>
        <p className="break-words rounded bg-canvas p-2 font-mono text-sm tracking-wider dark:bg-neutral-800" aria-hidden="true" data-testid="setup-key-grouped">
          {groupSecret(secret).join(" ")}
        </p>
        <p className="text-xs text-neutral-600 dark:text-neutral-400" data-testid="setup-key-label">
          If the app asks for a name, use <strong className="break-all text-ink dark:text-white">{host}</strong>, and <strong className="break-all text-ink dark:text-white">{email.trim()}</strong> for the account.
        </p>
        <p className="sr-only" data-testid="setup-key-spelled">
          The setup key, spelled out: {spellSecret(secret)}.
        </p>
        <button type="button" className={btnSecondary} onClick={() => void copy()}>
          Copy the setup key
        </button>
        <p role="status" className="text-xs">
          {copied === "yes" ? "The setup key is copied. Paste it into your authenticator app." : copied === "no" ? "The setup key couldn't be copied. Type it into your authenticator app instead." : ""}
        </p>
        <p className="text-sm">
          <a href={otpauth} className="inline-flex min-h-6 items-center underline">
            Open in an authenticator app on this device
          </a>
        </p>
      </div>
    </div>
  );
}

/** Mock builds only: the personas, one click each, for the rest of the e2e and local development. */
function MockPersonas({ from }: { from: string }) {
  const go = (...args: Parameters<typeof setMockSession>) => {
    setMockSession(...args);
    window.location.assign(from);
  };
  const button = `${btnSecondary} w-full py-3 text-base`;
  return (
    <div className="mt-8 space-y-2 border-t border-neutral-200 pt-6 dark:border-neutral-800">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Mock sign-in</p>
      <button type="button" className={button} onClick={() => go("internal-team")}>
        Continue as internal team
      </button>
      <button type="button" className={button} onClick={() => go("customer", "org-acme", ["cust-acme"])}>
        Continue as customer (ACME)
      </button>
      <button type="button" className={button} onClick={() => go("customer", "org-acme", ["cust-acme"], "org-acme")}>
        Continue as customer (ACME, SSO)
      </button>
      {/* ADR-0017: the two least-permitted people, so every hidden control can be checked by hand. */}
      <button type="button" className={button} onClick={() => go("customer", "org-acme", ["cust-acme"], undefined, "viewer")}>
        Continue as customer (ACME, viewer)
      </button>
      <button type="button" className={button} onClick={() => go("internal-team", undefined, undefined, undefined, "reader")}>
        Continue as internal team (reader)
      </button>
    </div>
  );
}
